import { useState, useEffect } from 'react';
import api from '@/lib/api';

const PUBLIC_VAPID_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';
const API_TIMEOUT = 10000; // 10 seconds

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

// Helper to race a promise against a timeout
function withTimeout(promise, ms, errorMessage) {
    return Promise.race([
        promise,
        new Promise((_, reject) =>
            setTimeout(() => reject(new Error(errorMessage)), ms)
        ),
    ]);
}

export function usePushNotifications(basePath = '/customer/push') {
    const [isSupported, setIsSupported] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [permission, setPermission] = useState('default'); // 'default' | 'granted' | 'denied'
    const [preferences, setPreferences] = useState({
        classes: true,
        cancellations: true,
        card_expiration: true,
        water_reminder: true,
        card_expiration_days: 5,
    });
    const [loading, setLoading] = useState(true);
    const [subscribing, setSubscribing] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
        setIsSupported(supported);

        if (supported) {
            setPermission(Notification.permission);
            checkSubscription();
        } else {
            setLoading(false);
        }
    }, []);

    const checkSubscription = async () => {
        try {
            let hasBrowserSubscription = false;

            // Check if there's a push subscription on THIS device
            if ('serviceWorker' in navigator) {
                try {
                    const registrations = await withTimeout(
                        navigator.serviceWorker.getRegistrations(),
                        5000,
                        'Timeout al verificar service workers'
                    );
                    for (const registration of registrations) {
                        if (registration.pushManager) {
                            try {
                                const sub = await withTimeout(
                                    registration.pushManager.getSubscription(),
                                    5000,
                                    'Timeout al obtener suscripción push'
                                );
                                if (sub) {
                                    hasBrowserSubscription = true;
                                    break;
                                }
                            } catch (subErr) {
                                console.error('Error getting push subscription:', subErr);
                            }
                        }
                    }
                } catch (swErr) {
                    console.error('Error checking service worker registrations:', swErr);
                }
            }

            const res = await withTimeout(
                api.get(`${basePath}/preferences`),
                API_TIMEOUT,
                'Timeout al obtener preferencias del servidor'
            );

            // Only consider subscribed if the browser has a subscription AND the server says enabled
            setIsSubscribed(hasBrowserSubscription && res.data.enabled);

            if (res.data.preferences) {
                setPreferences(res.data.preferences);
            }
        } catch (err) {
            console.error('Error checking push subscription:', err);
            setError(err.message || 'Error al verificar suscripción.');
        } finally {
            setLoading(false);
        }
    };

    const registerServiceWorker = async () => {
        if (!('serviceWorker' in navigator)) return null;
        try {
            const registration = await withTimeout(
                navigator.serviceWorker.register('/sw.js'),
                10000,
                'Timeout al registrar service worker'
            );
            return registration;
        } catch (err) {
            console.error('Service Worker registration failed:', err);
            setError('No se pudo registrar el service worker.');
            return null;
        }
    };

    const requestPermission = async () => {
        setError(null);

        // If already denied, we cannot ask again via code - user must reset in browser settings
        if (Notification.permission === 'denied') {
            setError('Las notificaciones están bloqueadas. Habilítalas manualmente desde la configuración del navegador.');
            return false;
        }

        // Request permission explicitly
        if (Notification.permission === 'default') {
            const result = await Notification.requestPermission();
            setPermission(result);

            if (result === 'denied') {
                setError('Permiso denegado. Habilítalo desde la configuración del navegador si cambiás de opinión.');
                return false;
            }
        }

        return Notification.permission === 'granted';
    };

    const subscribe = async () => {
        setError(null);
        setSubscribing(true);

        try {
            // Step 1: Request notification permission
            const hasPermission = await requestPermission();
            if (!hasPermission) return false;

            // Step 2: Register service worker
            const registration = await registerServiceWorker();
            if (!registration) return false;

            // Step 3: Wait for service worker to be ready
            await withTimeout(
                navigator.serviceWorker.ready,
                10000,
                'Timeout esperando service worker'
            );

            // Step 4: Subscribe to push
            let sub = await registration.pushManager.getSubscription();

            if (!sub) {
                const vapidKey = PUBLIC_VAPID_KEY || (await api.get(`${basePath}/vapid-public-key`)).data?.public_key;
                if (!vapidKey) {
                    throw new Error('VAPID public key not available');
                }

                sub = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(vapidKey),
                });
            }

            // Step 5: Send subscription to server
            const subscriptionData = {
                endpoint: sub.endpoint,
                publicKey: arrayBufferToBase64(sub.getKey('p256dh')),
                authToken: arrayBufferToBase64(sub.getKey('auth')),
                contentEncoding: 'aes128gcm',
            };

            await withTimeout(
                api.post(`${basePath}/subscribe`, subscriptionData),
                API_TIMEOUT,
                'Timeout al enviar suscripción al servidor'
            );

            setIsSubscribed(true);
            return true;
        } catch (err) {
            console.error('Push subscription failed:', err);
            setError(err.message || 'Error al suscribirse a notificaciones push.');
            return false;
        } finally {
            setSubscribing(false);
        }
    };

    const unsubscribe = async () => {
        setError(null);
        setSubscribing(true);

        try {
            let unsubscribed = false;

            if ('serviceWorker' in navigator) {
                try {
                    const registrations = await withTimeout(
                        navigator.serviceWorker.getRegistrations(),
                        5000,
                        'Timeout al verificar service workers'
                    );

                    for (const registration of registrations) {
                        if (!registration.pushManager) continue;

                        try {
                            const sub = await withTimeout(
                                registration.pushManager.getSubscription(),
                                5000,
                                'Timeout al obtener suscripción push'
                            );

                            if (sub) {
                                await withTimeout(
                                    api.post(`${basePath}/unsubscribe`, { endpoint: sub.endpoint }),
                                    API_TIMEOUT,
                                    'Timeout al notificar desuscripción al servidor'
                                );
                                await sub.unsubscribe();
                                unsubscribed = true;
                            }
                        } catch (subErr) {
                            console.error('Error processing subscription:', subErr);
                        }
                    }
                } catch (swErr) {
                    console.error('Error checking service worker registrations:', swErr);
                }
            }

            // If we couldn't find a local subscription but the server says we're subscribed,
            // send a global unsubscribe to clean up server-side state
            if (!unsubscribed) {
                await withTimeout(
                    api.post(`${basePath}/unsubscribe`),
                    API_TIMEOUT,
                    'Timeout al notificar desuscripción al servidor'
                );
            }

            setIsSubscribed(false);
            return true;
        } catch (err) {
            console.error('Push unsubscription failed:', err);
            setError(err.message || 'Error al desuscribirse.');
            return false;
        } finally {
            setSubscribing(false);
        }
    };

    const updatePreferences = async (newPreferences) => {
        try {
            await withTimeout(
                api.put(`${basePath}/preferences`, newPreferences),
                API_TIMEOUT,
                'Timeout al guardar preferencias'
            );
            setPreferences((prev) => ({ ...prev, ...newPreferences }));
            return true;
        } catch (err) {
            console.error('Error updating preferences:', err);
            setError('Error al guardar preferencias.');
            return false;
        }
    };

    return {
        isSupported,
        isSubscribed,
        permission,
        preferences,
        loading,
        subscribing,
        error,
        subscribe,
        unsubscribe,
        requestPermission,
        updatePreferences,
    };
}

function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
}
